import React, { useState } from 'react';
import type { ProductMedia } from '@noths/polaris-design-system-ooux-interfaces';

import * as styles from './ProductCardMedia.styles';

/**
 * How many of a product's images the card will carry.
 *
 * A listing renders 60 cards, so an unusually well-photographed product must not
 * quietly cost the page a dozen image requests. Five is what the design draws.
 */
export const MAX_IMAGES = 5;

interface ProductCardMediaProps {
  /** The product's ordered media. Images are used in order; video is skipped. */
  media: ProductMedia[];
  /** Called when a tap target over an image is clicked. The card's product click. */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Called on mousedown on a tap target. The card's product mousedown. */
  onLinkMouseDown?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Load the first image up front, for a card above the fold. */
  priority: boolean;
  /** The product's URL, so a tap on the image opens the product. */
  url: string;
}

/**
 * The card's image area: the images, the swipe between them, and the dots.
 *
 * Rendered inside the card's media element, which is the positioning context the
 * dots and the hover image rely on.
 *
 * Video is skipped for now. The design marks a video's position in the dots with a
 * triangle, but `ProductMedia` carries no poster URL, so a video slide has no frame
 * to show until that contract grows one.
 */
export const ProductCardMedia = ({
  media,
  onLinkClick,
  onLinkMouseDown,
  priority,
  url,
}: ProductCardMediaProps) => {
  const images = media.filter(({ type }) => type === 'image').slice(0, MAX_IMAGES);

  /* Most products have one image, and one image is not a carousel: there is nothing
     to swipe to, nothing to hover to, and nothing to count with dots. */
  const isCarousel = images.length > 1;

  const [scrolledTo, setScrolledTo] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenHovered, setHasBeenHovered] = useState(false);

  const hasHoverImage = hasBeenHovered && isCarousel;
  /* While the second image is showing, the dots have to agree with it. Still needed
     even though the dots are hidden wherever there is no touchscreen: a touchscreen
     laptop shows them and hovers, so it is the one device that does both. */
  const onScreen = isHovered && hasHoverImage ? 1 : scrolledTo;

  if (images.length === 0) {
    return <span css={styles.imagePlaceholder} />;
  }

  /* Slides are exactly one track wide, so the index is arithmetic rather than an
     IntersectionObserver: 60 cards would otherwise mean 300 observed elements.
     React bails out when the index has not changed, so this needs no throttling. */
  const handleScroll = ({ currentTarget }: React.UIEvent<HTMLDivElement>) => {
    const { clientWidth, scrollLeft } = currentTarget;

    if (clientWidth > 0) {
      setScrolledTo(Math.round(scrollLeft / clientWidth));
    }
  };

  /* Touch swipes instead, and should never pay for a second image. Anything else
     that reports a pointer — a mouse, a pen, or a browser that says nothing at
     all — can hover, so it gets the reveal. */
  const handlePointerEnter = ({ pointerType }: React.PointerEvent<HTMLDivElement>) => {
    if (pointerType !== 'touch') {
      setHasBeenHovered(true);
      setIsHovered(true);
    }
  };

  /**
   * Everything that only makes sense for a set of images, kept off a card that has
   * one.
   *
   * A single image gets a plain container: no carousel or slide semantics to
   * announce a carousel of one to a screen reader, and none of the scrolling
   * handlers or styles. The styles are the part with teeth. `overflow-x: auto` makes
   * an element a scroll container whether or not its content overflows, and
   * `overscroll-behavior-x: contain` on a scroll container stops a pan chaining to
   * an ancestor, so a card with one image would take a touch on its image, refuse to
   * pass the gesture on, and have nothing of its own to scroll. The card could not
   * be swiped from its image inside a horizontally scrollable parent.
   */
  const carouselProps: React.ComponentProps<'div'> = isCarousel
    ? {
        'aria-label': 'Product images',
        'aria-roledescription': 'carousel',
        onPointerEnter: handlePointerEnter,
        onPointerLeave: () => setIsHovered(false),
        onScroll: handleScroll,
        role: 'group',
      }
    : {};

  const slideProps = (index: number): React.ComponentProps<'div'> =>
    isCarousel
      ? {
          'aria-label': `${index + 1} of ${images.length}`,
          'aria-roledescription': 'slide',
          role: 'group',
        }
      : {};

  return (
    <>
      <div
        {...carouselProps}
        css={isCarousel ? styles.track : styles.imageRow}
        data-testid="product-card-media-track"
      >
        {images.map(({ alt, url: imageUrl }, index) => (
          <div
            {...slideProps(index)}
            css={isCarousel ? styles.slide : styles.frame}
            key={`${imageUrl}-${index}`}
          >
            <img
              alt={alt}
              css={styles.image}
              loading={priority && index === 0 ? 'eager' : 'lazy'}
              src={imageUrl}
              /* React 18 only applies the lowercase attribute; the camelCase form
                 warns and is dropped. Spread so TypeScript allows it. */
              {...(priority && index === 0 ? { fetchpriority: 'high' } : {})}
            />
            {/* Empty and aria-hidden on purpose: a tap target over the image, not a
                link a screen reader should find. The card's one link is the title.
                Unfocusable, so nothing hidden from the tree can still be tabbed to. */}
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a
              aria-hidden
              css={styles.slideLink}
              data-testid="product-card-media-link"
              href={url}
              onClick={onLinkClick}
              onMouseDown={onLinkMouseDown}
              tabIndex={-1}
            />
          </div>
        ))}
      </div>

      {hasHoverImage && (
        <img
          alt=""
          aria-hidden
          css={styles.hoverImage}
          data-testid="product-card-media-hover-image"
          data-visible={isHovered}
          src={images[1].url}
        />
      )}

      {isCarousel && (
        <span aria-hidden css={styles.dots} data-testid="product-card-media-dots">
          {images.map(({ url: imageUrl }, index) => (
            <span css={styles.dot} data-active={index === onScreen} key={`${imageUrl}-${index}`} />
          ))}
        </span>
      )}
    </>
  );
};

ProductCardMedia.displayName = 'ProductCardMedia';
